import React, { useState, useEffect } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { TiTick } from "react-icons/ti";
import { MdChildCare } from "react-icons/md";
import { IoPerson } from "react-icons/io5";
import { FaPlus, FaMinus } from "react-icons/fa";
import { getPrice } from "../../services/priceUtils";
import { useCurrency } from '../../context/CurrencyContext';
import { Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BigRateCard = ({ rate, thumbnail, roomName, checkIn, checkOut, occupancies = [] }) => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem('user_data'));
    const [prices, setPrices] = useState({
        total: "0.00",
        cancellation: "0.00"
    });
    const [cancellationPrices, setCancellationPrices] = useState({});
    const [selectedRate, setSelectedRate] = useState(null);
    const { selectedCurrency } = useCurrency();
    const [isRoomTypeAdded, setIsRoomTypeAdded] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    console.log('userData: ',userData);

    // Get quantity from cartSummary
    const getQuantity = () => {
        const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
        const itemInCart = cart.data.find(item => item.offerKey === rate.offerKey);
        return itemInCart ? itemInCart.quantity || 1 : 0;
    };

    // Toplam aranan kişi sayısını hesapla
    const getTotalPeopleSearched = () => {
        return occupancies.reduce((total, occupancy) => 
            total + occupancy.adults + occupancy.children, 0);
    };

    // Sepetteki toplam kişi sayısını hesapla
    const getTotalPeopleInCart = () => {
        const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
        return cart.data.reduce((total, item) => 
            total + (item.adults + item.children) * item.quantity, 0);
    };

    // URL'deki toplam kişi sayısını hesapla
    const getTotalPeopleFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const occupanciesStr = urlParams.get('occupancies');
        if (!occupanciesStr) return 0;
        
        const occupancies = JSON.parse(occupanciesStr);
        return occupancies.reduce((total, occ) => 
            total + (occ.adults + occ.children), 0);
    };

    // URL'deki aynı kişi sayısına sahip oda sayısını bul
    const getSameOccupancyCountInUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const occupanciesStr = urlParams.get('occupancies');
        if (!occupanciesStr) return 0;
        
        const occupancies = JSON.parse(occupanciesStr);
        return occupancies.filter(occ => 
            occ.adults === rate.adults && 
            occ.children === rate.children
        ).length;
    };

    // Sepetteki aynı kişi sayısına sahip odaların toplam sayısını hesapla
    const getSameOccupancyCountInCart = () => {
        const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
        return cart.data.reduce((total, item) => {
            if (item.adults === rate.adults && item.children === rate.children) {
                return total + (item.quantity || 1);
            }
            return total;
        }, 0);
    };

    // Sepete ekleme butonunun disabled durumunu kontrol et
    const isAddButtonDisabled = () => {
        const sameOccupancyCountInCart = getSameOccupancyCountInCart();
        const sameOccupancyCountInUrl = getSameOccupancyCountInUrl();
        
        return sameOccupancyCountInCart >= sameOccupancyCountInUrl;
    };

    const handleQuantityChange = (newQuantity) => {
        if (newQuantity < 0) return;
        
        const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
        const itemIndex = cart.data.findIndex(item => item.offerKey === rate.offerKey);
        
        if (itemIndex !== -1) {
            // Eğer miktar azaltılıyorsa
            if (newQuantity < cart.data[itemIndex].quantity) {
                cart.data[itemIndex].quantity = newQuantity;
                localStorage.setItem('cart', JSON.stringify(cart));
                // Storage event'ini tetikle
                window.dispatchEvent(new Event('storage'));
                
                // Eğer miktar 0'a düşürüldüyse, item'ı sepetten kaldır
                if (newQuantity === 0) {
                    cart.data.splice(itemIndex, 1);
                    localStorage.setItem('cart', JSON.stringify(cart));
                    window.dispatchEvent(new Event('storage'));
                }
            } else {
                // Miktar artırılıyorsa, limit kontrolü yap
                const sameOccupancyCountInCart = getSameOccupancyCountInCart();
                const sameOccupancyCountInUrl = getSameOccupancyCountInUrl();
                
                if (sameOccupancyCountInCart < sameOccupancyCountInUrl) {
                    cart.data[itemIndex].quantity = newQuantity;
                    localStorage.setItem('cart', JSON.stringify(cart));
                    window.dispatchEvent(new Event('storage'));
                }
            }
        }

        Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 1000,
            timerProgressBar: true,
            icon: 'success',
            title: 'Room has been decreased',
            background: '#fff',
            iconColor: '#28a745',
            customClass: {
                popup: 'custom-toast',
                title: 'toast-title'
            }
        });
    };

    useEffect(() => {
        // Check if this room type is already in the basket
        const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
        const guestSelection = JSON.parse(localStorage.getItem('guest_selection')) || { rooms: [] };
        
        // Find matching room type in guest selection
        const matchingRoom = guestSelection.rooms.find(room => 
            room.adultCounter === rate.adults && 
            room.childCounter === rate.children &&
            JSON.stringify(room.childrenAges) === JSON.stringify(rate.childrenAges)
        );

        if (matchingRoom) {
            // Check if any room with the same occupancy is in cart
            const isAdded = cart.data.some(item => 
                item.adults === rate.adults &&
                item.children === rate.children &&
                JSON.stringify(item.childrenAges) === JSON.stringify(rate.childrenAges)
            );
            setIsRoomTypeAdded(isAdded);
        }
    }, [rate]);

    const handleAddToBasket = async () => {
        try {
            if (!userData?.token) {
                Swal.fire({
                    title: 'Please Login',
                    text: 'You need to login first to add items to cart',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return;
            }

            // Kişi sayısı kontrolü
            const totalPeopleInCart = getTotalPeopleInCart();
            const totalPeopleFromUrl = getTotalPeopleFromUrl();
            const currentRoomPeople = rate.adults + rate.children;

            if (totalPeopleInCart + currentRoomPeople > totalPeopleFromUrl) {
                Swal.fire({
                    title: 'Limit Reached',
                    text: 'You have already added the maximum number of rooms for your search criteria',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return;
            }

            // Aynı kişi sayısına sahip oda kontrolü
            const sameOccupancyCountInCart = getSameOccupancyCountInCart();
            const sameOccupancyCountInUrl = getSameOccupancyCountInUrl();

            if (sameOccupancyCountInCart >= sameOccupancyCountInUrl) {
                Swal.fire({
                    title: 'Limit Reached',
                    text: `You have already added the maximum number of ${rate.adults} adult ${rate.children > 0 ? `and ${rate.children} child` : ''} rooms`,
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return;
            }

            // Get current cart
            const cart = JSON.parse(localStorage.getItem('cart')) || { data: [] };
            
            // Check if item already exists in cart
            const existingItemIndex = cart.data.findIndex(item => item.offerKey === rate.offerKey);
            
            if (existingItemIndex !== -1) {
                // Update quantity if item exists
                cart.data[existingItemIndex].quantity = (cart.data[existingItemIndex].quantity || 1) + 1;
            } else {
                // Add new item if it doesn't exist
                cart.data.push({
                    offerKey: rate.offerKey,
                    hotel: rate.hotel,
                    room: rate.room,
                    checkIn: checkIn,
                    checkOut: checkOut,
                    adults: rate.adults,
                    children: rate.children,
                    roomName: roomName,
                    boardName: rate.boardName,
                    nights: rate.nights,
                    price: prices.total,
                    quantity: 1
                });
            }

            // Update cart in localStorage
            localStorage.setItem('cart', JSON.stringify(cart));
            
            // Trigger storage event to update other components
            window.dispatchEvent(new Event('storage'));

            // Update cart count in header
            const headerCart = document.querySelector('.header-cart-count');
            if (headerCart) {
                headerCart.textContent = cart.data.reduce((sum, item) => sum + (item.quantity || 1), 0);
            }

            // Show success message
            Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 1000,
                timerProgressBar: true,
                icon: 'success',
                title: existingItemIndex !== -1 ? 'Room quantity has been increased' : 'Room has been added to your cart',
                background: '#fff',
                iconColor: '#28a745',
                customClass: {
                    popup: 'custom-toast',
                    title: 'toast-title'
                }
            });
        } catch (error) {
            console.error('Error adding to cart:', error);
            Swal.fire({
                title: 'Error!',
                text: 'Failed to add to cart',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const formatCancellationDate = (dateString) => {
        const date = new Date(dateString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        
        const dateOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        if (hours === 3 && minutes === 0) {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at 23:59`;
        } else if (hours === 0 && minutes === 0) {
            return new Intl.DateTimeFormat('en-US', dateOptions).format(date);
        } else {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    };

    const calculateNights = () => {
        if (rate?.nights) {
            return rate.nights;
        }

        if (rate?.rateKey) {
            const dates = rate.rateKey.split('|');
            if (dates.length >= 2) {
                const checkIn = new Date(dates[0].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
                const checkOut = new Date(dates[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
                const diffTime = Math.abs(checkOut - checkIn);
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
        }
        return 1;
    };

    const [agencyId, setAgencyId] = useState(null);
    const nights = calculateNights();
    const perNightPrice = prices.total && nights ? (Number(prices.total) / nights).toFixed(2) : "0.00";

    useEffect(() => {
        const calculatePrices = async () => {
            const userData = JSON.parse(localStorage.getItem('user_data'));
            if (userData?.token && rate?.net) {
                try {
                    const [totalPrice, cancellationPrice] = await Promise.all([
                        getPrice(rate.net, userData.token, selectedCurrency, rate.taxes, rate?.country?.code, rate.currency),
                        rate?.cancellationPolicies?.[0]?.amount ?
                            getPrice(rate.cancellationPolicies[0].amount, userData.token, selectedCurrency, null, null, rate.currency) :
                            "0.00"
                    ]);

                    const newCancellationPrices = {};
                    if (rate?.cancellationPolicies?.length > 0) {
                        for (const policy of rate.cancellationPolicies) {
                            if (policy.amount) {
                                if (rate.currency === 'EUR') {
                                    newCancellationPrices[policy.from] = policy.amount;
                                } else {
                                    const convertedPrice = await getPrice(
                                        policy.amount,
                                        userData.token,
                                        selectedCurrency,
                                        null,
                                        null,
                                        rate.currency
                                    );
                                    newCancellationPrices[policy.from] = convertedPrice;
                                }
                            }
                        }
                    }

                    setPrices({
                        total: totalPrice || "0.00",
                        cancellation: cancellationPrice || "0.00"
                    });
                    setCancellationPrices(newCancellationPrices);
                } catch (error) {
                    console.error("Error calculating prices:", error);
                    setPrices({
                        total: "0.00",
                        cancellation: "0.00"
                    });
                    setCancellationPrices({});
                }
            }
        };
        calculatePrices();
        const storedAgencyId = localStorage.getItem('agency_id');
        setAgencyId(storedAgencyId ? Number(storedAgencyId) : null);
    }, [rate, selectedCurrency]);

    const getOccupancyText = (occupancy) => {
        const adults = occupancy.adults || 0;
        const children = occupancy.children || 0;
        let text = `${adults} ADULT${adults > 1 ? 'S' : ''}`;
        if (children > 0) {
            text += `, ${children} CHILD${children > 1 ? 'REN' : ''}`;
        }
        return text;
    };

    const handleSubmit = async () => {

        setIsSubmitting(true);
        try {
            const response = await axios.post(
                `${config.API_BASE_URL_V1}hotel/on-request/${rate.offerKey}`,
                { description },
                {
                    headers: {
                        'Authorization': `Bearer ${userData?.token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('RESPONSE', response);

            if (response.status === 200) {
                setShowRequestModal(false);
                setDescription('');
                Swal.fire({
                    title: 'Success',
                    text: 'Your request has been submitted successfully',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            console.error('Error submitting request:', error);
            Swal.fire({
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit request',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="rate-card-container" style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            marginBottom: "20px",
            overflow: "hidden",
            transition: "all 0.3s ease",
            border: "1px solid #E0E0E0"
        }}>
            <div className="room-header" style={{
                background: "linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)",
                padding: "5px 15px",
                borderBottom: "1px solid #E0E0E0",
                position: "relative"
            }}>
                <div className="room-title-container" style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap"
                }}>
                    <h4 className="room-title" style={{
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        color: "#2D3436",
                        margin: "0",
                        textTransform: "capitalize"
                    }}>
                        {roomName?.toUpperCase()}
                    </h4>
                    <div className="occupancy-info">
                        <div className="occupancy-count" style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                            flexWrap: "wrap"
                        }}>
                            <span className="adults-count" style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                background: "#E3F2FD",
                                color: "#1976D2",
                                fontSize: "0.85rem",
                                fontWeight: "500"
                            }}>
                                <IoPerson className="person-icon" />
                                {rate?.adults || 0} ADULT{rate?.adults > 1 ? 'S' : ''}
                            </span>
                            {rate?.children > 0 && (
                                <span className="children-count" style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "6px 12px",
                                    borderRadius: "20px",
                                    background: "#E8F5E9",
                                    color: "#2E7D32",
                                    fontSize: "0.85rem",
                                    fontWeight: "500"
                                }}>
                                    <MdChildCare className="child-icon" />
                                    {rate?.children} CHILD{rate?.children > 1 ? 'REN' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="rate-details" style={{
                padding: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap"
            }}>
                <div>
                    { userData?.user?.agency_id == 1 && userData?.user?.isOwner && (
                        <div>
                            {
                                rate?.provider + ' -' 
                            }
                        </div>
                    )}
                </div>
                <div className="board-info" style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                }}>
                    <span className="board-badge" style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        background: "#F5F6FA",
                        color: "#2D3436",
                        fontSize: "0.9rem",
                        fontWeight: "500"
                    }}>
                        <i className="isax isax-coffee"></i>
                        <span className="board-name text-capitalize">{rate?.boardName?.toLowerCase() || 'N/A'}</span>
                    </span>
                </div>

                <div className="cancellation-policy" style={{
                    flex: "1",
                    minWidth: "200px"
                }}>
                    {rate?.rateClass === "NOR" ? (
                        rate?.cancellationPolicies?.length > 0 ? (
                            <button 
                                className="cancellation-button" 
                                onClick={() => setSelectedRate(rate)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 16px",
                                    borderRadius: "20px",
                                    background: "#E8F5E9",
                                    color: "#2E7D32",
                                    border: "none",
                                    fontSize: "0.9rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease"
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.background = "#C8E6C9";
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.background = "#E8F5E9";
                                }}
                            >
                                <TiTick className="tick-icon" />
                                <span>
                                    Free Cancellation until {formatCancellationDate(rate.cancellationPolicies[0].from)}
                                </span>
                            </button>
                        ) : (
                            <button 
                                className="cancellation-button non-refundable"
                                onClick={() => setSelectedRate(rate)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 16px",
                                    borderRadius: "20px",
                                    background: "#FFEBEE",
                                    color: "#D32F2F",
                                    border: "none",
                                    fontSize: "0.9rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease"
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.background = "#FFCDD2";
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.background = "#FFEBEE";
                                }}
                            >
                                <ImCross className="cross-icon" />
                                <span>Non-Refundable</span>
                            </button>
                        )
                    ) : (
                        <button 
                            className="cancellation-button non-refundable"
                            onClick={() => setSelectedRate(rate)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 16px",
                                borderRadius: "20px",
                                background: "#FFEBEE",
                                color: "#D32F2F",
                                border: "none",
                                fontSize: "0.9rem",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "all 0.3s ease"
                            }}
                            onMouseOver={(e) => {
                                e.target.style.background = "#FFCDD2";
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = "#FFEBEE";
                            }}
                        >
                            <ImCross className="cross-icon" />
                            <span>Non-Refundable</span>
                        </button>
                    )}
                </div>

                <div className="price-section" style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "8px",
                    minWidth: "180px"
                }}>
                    <div className="price-details">
                        <h5 className="total-price" style={{
                            color: "#0984E3",
                            margin: "0",
                            fontSize: "1.5rem",
                            fontWeight: "600"
                        }}>
                            {prices.total} {selectedCurrency}
                            <span className="nights" style={{
                                color: "#636E72",
                                fontSize: "0.9rem",
                                fontWeight: "normal",
                                marginLeft: "4px"
                            }}>
                                / {rate?.nights || 1} {(rate?.nights || 1) === 1 ? 'Night' : 'Nights'}
                            </span>
                        </h5>
                        <small className="per-night" style={{
                            color: "#636E72",
                            fontSize: "0.85rem"
                        }}>
                            {(Number(prices.total) / (rate?.nights || 1)).toFixed(2)} {selectedCurrency} per night
                        </small>
                    </div>

                    {
                        rate?.isOnRequest ?
                        <>
                            <button
                                type="button"
                                className="btn btn-primary"
                                style={{ 
                                whiteSpace: "nowrap",
                                ...(rate?.isOnRequest && {
                                    backgroundColor: '#e3f2fd',
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                    color: '#1976d2',
                                    border: '1px solid #90caf9',
                                    boxShadow: '0 4px 6px rgba(25, 118, 210, 0.1)',
                                    borderRadius:'10px'
                                })
                                }}
                                disabled={!prices.total || prices.total === "0.00" || !(rate?.rateKey || rate?.key || rate?.offerKey)}
                                onClick={() => setShowRequestModal(true)}
                            >
                                On Request
                            </button>
                        </>
                        : 
                        <>
                        <div className="quantity-control" style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "8px"
                        }}>
                        <button 
                            className="quantity-button minus"
                            onClick={() => handleQuantityChange(getQuantity() - 1)}
                            disabled={getQuantity() <= 0}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "8px",
                                border: "1px solid #E0E0E0",
                                background: "white",
                                color: "#2D3436",
                                cursor: "pointer",
                                transition: "all 0.3s ease"
                            }}
                            onMouseOver={(e) => {
                                if (!e.target.disabled) {
                                    e.target.style.background = "#F8F9FA";
                                    e.target.style.borderColor = "#0984E3";
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!e.target.disabled) {
                                    e.target.style.background = "white";
                                    e.target.style.borderColor = "#E0E0E0";
                                }
                            }}
                        >
                            <FaMinus />
                        </button>
                        <span className="quantity" style={{
                            flex: "1",
                            textAlign: "center",
                            fontSize: "1rem",
                            fontWeight: "600",
                            color: "#2D3436",
                            minWidth: "40px"
                        }}>
                            {getQuantity()}
                        </span>
                        <button 
                            className="quantity-button plus"
                            onClick={handleAddToBasket}
                            disabled={isAddButtonDisabled()}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "8px",
                                border: "1px solid #E0E0E0",
                                background: "white",
                                color: "#2D3436",
                                cursor: "pointer",
                                transition: "all 0.3s ease"
                            }}
                            onMouseOver={(e) => {
                                if (!e.target.disabled) {
                                    e.target.style.background = "#F8F9FA";
                                    e.target.style.borderColor = "#0984E3";
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!e.target.disabled) {
                                    e.target.style.background = "white";
                                    e.target.style.borderColor = "#E0E0E0";
                                }
                            }}
                        >
                            <FaPlus />
                        </button>
                        </div>
                        </>
                    }

                    
                </div>
            </div>

            <Modal
                show={selectedRate !== null}
                onHide={() => setSelectedRate(null)}
                centered
                className="cancellation-modal"
            >
                <Modal.Header closeButton style={{
                    borderBottom: "1px solid #E0E0E0",
                    padding: "20px"
                }}>
                    <h6 style={{
                        color: '#0984E3',
                        fontSize: '1.2rem',
                        fontWeight: '500',
                        margin: '0'
                    }}>Cancellation Policy</h6>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedRate?.cancellationPolicies?.length > 0 ? (
                        selectedRate.cancellationPolicies
                            .filter(policy => Number(policy.amount) > 0)
                            .map((policy, index) => (
                                <div key={index} className="cancellation-policy-container" style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "16px",
                                    marginBottom: "16px"
                                }}>
                                    <div className="modern-badge free-cancellation" style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                                        boxShadow: "0 2px 8px rgba(76, 175, 80, 0.15)"
                                    }}>
                                        <div className="badge-icon" style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "12px",
                                            background: "#4CAF50",
                                            color: "white",
                                            marginRight: "16px",
                                            flexShrink: "0"
                                        }}>
                                            <TiTick />
                                        </div>
                                        <div className="badge-content">
                                            <h6 className="badge-title" style={{
                                                fontSize: "1.1rem",
                                                fontWeight: "600",
                                                color: "#2E7D32",
                                                marginBottom: "8px"
                                            }}>Free Cancellation Period</h6>
                                            <p className="badge-text" style={{
                                                fontSize: "0.95rem",
                                                color: "#1B5E20",
                                                margin: "0",
                                                lineHeight: "1.5"
                                            }}>
                                                Cancel without any charges until {formatCancellationDate(policy.from)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="modern-badge partial-charge" style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        background: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
                                        boxShadow: "0 2px 8px rgba(255, 152, 0, 0.15)"
                                    }}>
                                        <div className="badge-icon" style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "12px",
                                            background: "#FF9800",
                                            color: "white",
                                            marginRight: "16px",
                                            flexShrink: "0"
                                        }}>
                                            <FaInfoCircle />
                                        </div>
                                        <div className="badge-content">
                                            <h6 className="badge-title" style={{
                                                fontSize: "1.1rem",
                                                fontWeight: "600",
                                                color: "#E65100",
                                                marginBottom: "8px"
                                            }}>Cancellation Fee Period</h6>
                                            <p className="badge-text" style={{
                                                fontSize: "0.95rem",
                                                color: "#F57C00",
                                                margin: "0",
                                                lineHeight: "1.5"
                                            }}>
                                                From {formatCancellationDate(policy.from)} to {formatCancellationDate(checkIn)}<br/>
                                                <span style={{ fontWeight: "600" }}>
                                                    Fee: {cancellationPrices[policy.from] || "0.00"} {selectedCurrency}
                                                </span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="modern-badge non-refundable" style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        background: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
                                        boxShadow: "0 2px 8px rgba(244, 67, 54, 0.15)"
                                    }}>
                                        <div className="badge-icon" style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "12px",
                                            background: "#F44336",
                                            color: "white",
                                            marginRight: "16px",
                                            flexShrink: "0"
                                        }}>
                                            <ImCross />
                                        </div>
                                        <div className="badge-content">
                                            <h6 className="badge-title" style={{
                                                fontSize: "1.1rem",
                                                fontWeight: "600",
                                                color: "#D32F2F",
                                                marginBottom: "8px"
                                            }}>Non-Refundable Period</h6>
                                            <p className="badge-text" style={{
                                                fontSize: "0.95rem",
                                                color: "#B71C1C",
                                                margin: "0",
                                                lineHeight: "1.5"
                                            }}>
                                                After {formatCancellationDate(checkIn)}<br/>
                                                <span style={{ fontWeight: "600" }}>
                                                    Full amount will be charged
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                    ) : (
                        <div className="cancellation-policy-container" style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            marginBottom: "16px"
                        }}>
                            <div className="modern-badge non-refundable" style={{
                                display: "flex",
                                alignItems: "flex-start",
                                padding: "20px",
                                borderRadius: "16px",
                                background: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
                                boxShadow: "0 2px 8px rgba(244, 67, 54, 0.15)"
                            }}>
                                <div className="badge-icon" style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "12px",
                                    background: "#F44336",
                                    color: "white",
                                    marginRight: "16px",
                                    flexShrink: "0"
                                }}>
                                    <ImCross />
                                </div>
                                <div className="badge-content">
                                    <h6 className="badge-title" style={{
                                        fontSize: "1.1rem",
                                        fontWeight: "600",
                                        color: "#D32F2F",
                                        marginBottom: "8px"
                                    }}>Non-Refundable Booking</h6>
                                    <p className="badge-text" style={{
                                        fontSize: "0.95rem",
                                        color: "#B71C1C",
                                        margin: "0",
                                        lineHeight: "1.5"
                                    }}>
                                        Non-refundable - If cancelled, full charge: <span style={{ fontWeight: "700", fontSize: "1.05rem" }}>{prices.total || "0.00"} {selectedCurrency}</span> - No refund
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={showRequestModal}
                onHide={() => {
                    setShowRequestModal(false);
                    setDescription('');
                }}
                centered
                className="request-modal modal-lg"
            >
                <Modal.Header closeButton style={{
                    borderBottom: "1px solid #E0E0E0",
                    padding: "20px",
                    textAlign: 'center'
                }}>
                    <div className="d-flex flex-column justify-content-center">
                        <span style={{
                            color: '#0984E3',
                            fontSize: '1.2rem',
                            fontWeight: '500',
                            margin: '0',
                            textAlign: 'left'
                        }}>Request Room Information</span>
                        <p style={{
                            fontSize: '0.9rem',
                            color: '#636E72',
                            marginTop: '8px',
                            textAlign: 'left'
                        }}>Please fill out the form below in the following format.</p>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <ReactQuill
                        theme="snow"
                        style={{
                            height: '150px',
                            marginBottom: '20px'
                        }}
                        value={description}
                        onChange={setDescription}
                        required={true}
                        placeholder={`I would like a room for ${rate.adults} adults and ${rate.children} children from ${checkIn} to ${checkOut}.`}
                    />
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !description.trim()}
                        style={{
                            marginTop: '50px',
                            textAlign: 'right'
                        }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default BigRateCard;

// Global styles
const styles = `
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.rate-card-container {
    animation: fadeIn 0.3s ease-out;
}

.rate-card-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
}

.quantity-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .rate-details {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .price-section {
        width: 100%;
        align-items: flex-start !important;
    }
    
    .quantity-control {
        width: 100%;
        justify-content: flex-start;
    }
}
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
